import type {
  AgentFinancialOptionChartData,
  AgentFinancialOptionData,
  AgentFinancialResponse,
  AgentSource,
} from '../../../../api/events-client/events-client.type';
import type { AgentCostsCategory } from '../../../../store/financials-store/financials-store.type';
import type { DataPoint } from '../shared/shared.type';

/**
 * Aggregates the tasks array into 2 items for the "General" category:
 * - "Associated Tasks": merged cost/tokens from all task-linked sessions
 * - "Unassigned": sessions without task associations
 *
 * @param {AgentFinancialOptionData[]} tasks - The tasks array from the API response
 * @returns {AgentFinancialOptionData[]} Two items: Associated Tasks and Unassigned
 */
const getGeneralCategoryData = (tasks: AgentFinancialOptionData[]): AgentFinancialOptionData[] => {
  const unassigned = tasks.find((item) => item.id === 'unassigned');
  const associatedTasks = tasks.filter((item) => item.id !== 'unassigned');

  const mergedChartData: Record<string, AgentFinancialOptionChartData> = {};
  for (const task of associatedTasks) {
    for (const [date, dateData] of Object.entries(task.chart_data)) {
      if (!mergedChartData[date]) {
        mergedChartData[date] = {
          cost: { total: 0, by_agent_source: {} as Record<AgentSource, number> },
          tokens: { total: 0, by_agent_source: {} as Record<AgentSource, number> },
        };
      }
      mergedChartData[date].cost.total += dateData.cost.total;
      mergedChartData[date].tokens.total += dateData.tokens.total;
      for (const [source, value] of Object.entries(dateData.cost.by_agent_source)) {
        mergedChartData[date].cost.by_agent_source[source as AgentSource] =
          (mergedChartData[date].cost.by_agent_source[source as AgentSource] || 0) + value;
      }
      for (const [source, value] of Object.entries(dateData.tokens.by_agent_source)) {
        mergedChartData[date].tokens.by_agent_source[source as AgentSource] =
          (mergedChartData[date].tokens.by_agent_source[source as AgentSource] || 0) + value;
      }
    }
  }

  const result: AgentFinancialOptionData[] = [];
  if (associatedTasks.length > 0) {
    result.push({ id: 'associated_tasks', name: 'Task-Associated', chart_data: mergedChartData });
  }
  if (unassigned) {
    result.push(unassigned);
  }
  return result;
};

/**
 * Returns the category items for the given category, handling the "general" aggregation.
 *
 * @param {AgentFinancialResponse} data - The API response
 * @param {AgentCostsCategory} selectedCategory - The selected category
 * @returns {AgentFinancialOptionData[]} The items for the selected category
 */
const getCategoryItems = (
  data: AgentFinancialResponse,
  selectedCategory: AgentCostsCategory,
): AgentFinancialOptionData[] => {
  if (selectedCategory === 'general') {
    return getGeneralCategoryData(data.tasks);
  }
  return data[selectedCategory] || [];
};

/**
 * Builds a map from item ID to a unique display key. For items with duplicate
 * names, appends the first 5 characters of the ID in parentheses.
 *
 * @param {AgentFinancialOptionData[]} items - The category items
 * @returns {Record<string, string>} Map of item.id → unique display key
 */
const buildDisplayKeyMap = (items: AgentFinancialOptionData[]): Record<string, string> => {
  const nameCounts: Record<string, number> = {};
  for (const item of items) {
    const name = item.code ?? item.name;
    nameCounts[name] = (nameCounts[name] || 0) + 1;
  }

  const keyMap: Record<string, string> = {};
  for (const item of items) {
    const name = item.code ?? item.name;
    if (nameCounts[name] > 1) {
      keyMap[item.id] = `${name} (${item.id.substring(0, 5)})`;
    } else {
      keyMap[item.id] = name;
    }
  }
  return keyMap;
};

/**
 * Transforms the agent financial response data into chart-ready DataPoints.
 *
 * @param {AgentFinancialResponse} data - The raw agent financial response data
 * @param {AgentCostsCategory} selectedCategory - The selected category to chart
 * @param {AgentSource | 'all'} selectedAgentSource - The agent source to filter by, or 'all' for totals
 * @param {'cost' | 'tokens'} viewBy - Whether to chart by cost or token count
 * @returns {DataPoint[]} The transformed data points sorted chronologically
 */
const getAgentCostsChartData = (
  data: AgentFinancialResponse,
  selectedCategory: AgentCostsCategory,
  selectedAgentSource: AgentSource | 'all',
  viewBy: 'cost' | 'tokens',
): DataPoint[] => {
  const categoryData = getCategoryItems(data, selectedCategory);
  const keyMap = buildDisplayKeyMap(categoryData);

  const chartData =
    data?.dates
      ?.slice()
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
      .map((date) => {
        const dataPoint: Record<string, any> = { date };

        categoryData?.forEach((item: AgentFinancialOptionData) => {
          let value = 0;
          if (selectedAgentSource === 'all') {
            value = item.chart_data?.[date]?.[viewBy]?.total ?? 0;
          } else {
            value = item.chart_data?.[date]?.[viewBy]?.by_agent_source?.[selectedAgentSource] ?? 0;
          }
          dataPoint[keyMap[item.id]] = value;
        });

        return dataPoint as DataPoint;
      }) ?? [];

  return chartData;
};

export { buildDisplayKeyMap, getAgentCostsChartData, getCategoryItems };
